<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Schedule Data Loading...
  </div>
  <div class="animated fadeIn mb-5" v-else>
    <basic-heading v-if="schedule">
      <span class="float-left mr-2">{{
        `${schedule_process_prefix} - ${
          sample_type ? sample_type.name : "Type not specified"
        } - ${schedule.name()}`
      }}</span>
      <badge-batch :badge_collection="process_badges" />
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete schedule')"
              title="Delete schedule"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read schedule'">
      <template #gated-content>
        <status-progress
          :current_status="current_status"
          :log_collection="schedule_status_logs"
          status_group="schedule"
          class="mb-3"
        />
        <context-pills :items="context__items" class="mb-3">
          <template #before-pills>
<!--             <b-overlay
              :show="uploading"
              spinner-small
              spinner-variant="info"
              class="d-inline-block"
            >
              <context-pill
                :disabled="!approved"
                variant="dark"
                :onClick="upload()"
                faIcon="upload"
                label="Upload Results"
              />
            </b-overlay> -->
<!--             <context-pill
              :disabled="!approved"
              variant="dark"
              :to="`/schedules/${schedule.id}/results`"
              faIcon="table"
              label="Manual Results Enter"
            /> -->
<!--             <context-pill
              :disabled="!approved"
              variant="dark"
              :to="`/schedules/${schedule.id}/grinding-order`"
              faIcon="upload"
              label="Print Grinding Order"
            /> -->
            <context-pill
              variant="dark"
              :onClick="runAutoScheduler"
              :faIcon="!auto_schedule_running ? 'calendar-plus' : 'spinner'"
              :shouldAnimate="auto_schedule_running"
              :disabled="auto_schedule_running"
              label="Run Auto Schedule"
            />
            <context-pill 
              variant="dark"
              :onClick="clearAutoSchedule"
              faIcon="calendar-minus"
              label="Clear Auto Schedule"
              :disabled="auto_schedule_running"
            />
            <context-pill
              v-show="!approved"
              variant="success"
              :onClick="approve"
              :faIcon="!approved_loading ? 'check-circle' : 'spinner'"
              :shouldAnimate="approved_loading"
              label="Approve"
              :disabled="auto_schedule_running"
            />
            <context-pill
              v-show="schedule_status !== 'Draft'"
              variant="warning"
              :onClick="draft"
              faIcon="edit"
              label="Draft"
              :disabled="auto_schedule_running"
            />
            <context-pill
              v-show="schedule_status !== 'Declined'"
              variant="danger"
              :onClick="decline"
              faIcon="times-circle"
              label="Decline"
              :disabled="auto_schedule_running"
            />
          </template>
        </context-pills>
        <schedule-process-download
          :process_schedule="schedule.getProcessSchedules().first()"
          ref="processDownload"
        />
        <schedule-process-upload
          :process_schedule="schedule.getProcessSchedules().first()"
          ref="processUpload"
          @onUpload="uploading = true"
          @onUploadSuccess="uploading = false"
          @onUploadFailure="uploading = false"
        />
        <b-tabs>
          <b-tab title="Sample Cohort">
            <permission-alert-gate :permissions="'list schedule sample'">
              <template #gated-content>
                <schedule-cohort-table :sample_collection="sample_collection" />
              </template>
            </permission-alert-gate>
          </b-tab>
          <b-tab title="Instruments">
            <permission-alert-gate
              :permissions="'list instrument instance schedule'"
            >
              <template #gated-content>
                <b-row
                  v-for="(process, index) in schedule.getProcessSchedules()
                    .models"
                  :key="index"
                  class="pb-3"
                >
                  <b-col>
                    <div class="font-xl">
                      {{ `${process.schedulable_process.name} Instruments` }}
                    </div>
                    <b-row class="pr-3">
                      <b-col
                        v-for="(
                          proc_instrument_inst, index
                        ) in process.process_schedule_instrument_instances"
                        :key="index"
                        cols="4"
                        class="pr-0"
                      >
                        <b-row class="pr-3">
                          <b-col cols="6" class="pr-0">
                            <div>
                              {{ proc_instrument_inst.instrument ? proc_instrument_inst.instrument.name: 'Equipmentless' }}
                            </div>
                            <div
                              v-if="proc_instrument_inst.instrument_instance"
                            >
                              {{
                                proc_instrument_inst.instrument_instance
                                  .identifier
                              }}
                              <instrument-capacity-bar
                                class="d-inline-block w-100"
                                :process="process"
                                :instrument_instance="
                                  proc_instrument_inst.instrument_instance
                                "
                              />
                            </div>
                            <div v-else>Any</div>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </template>
            </permission-alert-gate>
          </b-tab>
          <b-tab
            v-for="(process, index) in schedule.process_schedules"
            :key="index"
            :title="process.schedulable_process.name"
          >
            <schedule-detail-process-tab
              :schedule="schedule"
              :schedule_process="process"
              :sample_collection="sample_collection"
              :instrument_jig_collection="instrument_jig_collection"
            />
          </b-tab>
        </b-tabs>

        <b-modal
          title="Are you sure you want to delete this schedule?"
          variant="warning"
          id="deleteWarning"
        >
          <template #default="{ hide }">
            <b-row>
              <b-col><b-button @click="hide()" block>No</b-button></b-col>
              <b-col
                ><b-button variant="danger" @click="deleteSchedule" block
                  >Yes</b-button
                ></b-col
              >
            </b-row>
          </template>
        </b-modal>
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import ActionButton from "@/components/shared/ActionButton";
import BadgeBatch from "@/components/shared/BadgeBatch";
import BasicHeading from "@/components/shared/BasicHeading";
import ContextPill from "@/components/shared/ContextPill";
import ContextPills from "@/components/shared/ContextPills";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import InstrumentCapacityBar from "@/components/lab/instrument/InstrumentCapacityBar";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import ScheduleCohortTable from "@/components/lab/schedule/ScheduleCohortTable";
import ScheduleDetailProcessTab from "@/components/lab/schedule/ScheduleDetailProcessTab";
import ScheduleProcessDownload from "@/components/lab/schedule/ScheduleProcessDownload";
import ScheduleProcessUpload from "@/components/lab/schedule/ScheduleProcessUpload";
import StatusProgress from "@/components/shared/StatusProgress";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { scheduleNotifications } from "@/shared/notifications";
import { getModel, getCollection } from "@/shared/fetch-utils";
import ScheduleModel from '@/models/lab/ScheduleModel';

Vue.use(VueNotifications, notificationOptions);

const ScheduleRepository = LabRepositoryFactory.get("schedule");

export default {
  name: "ScheduleDetail",
  mixins: [ToastNotifications],
  components: {
    ActionButton,
    BadgeBatch,
    BasicHeading,
    ContextPill,
    ContextPills,
    HeaderButtonGroup,
    InstrumentCapacityBar,
    PermissionAlertGate,
    ScheduleCohortTable,
    ScheduleDetailProcessTab,
    ScheduleProcessUpload,
    ScheduleProcessDownload,
    StatusProgress,
  },
  data() {
    return {
      loading: true,
      run_init: false,
      approved_loading: false,
      auto_schedule_running: false,
      schedule_id: this.$route.params.schedule_id || null,
      schedule: null,
      instrument_jig_collection: null,
      deleteWarning: false,
      sample_collection: null,
      sample_type: null,
      downloading: false,
      download_csv: false,
      uploading: false,
      context__items: [],
      schedule_status_logs: null,
      sample_table_cols: [
        "pid",
        "receiving_id",
        "submission.pid",
        "submission.project.customer.full_name",
        "identifier",
        "number",
        "created_at",
        "received",
        "prepped",
        "tested",
      ],
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/schedules",
            title: "Schedules",
          },
        ],
        dropdown: {
          items: [
            {
              icon: "fa-pencil",
              linkto: `/schedules/${this.schedule_id}/sample-cohort`,
              text: "Manage Sample Cohort",
            },
            {
              icon: "fa-tools",
              linkto: `/schedules/${this.schedule_id}/schedule-samples`,
              text: "Manage Schedule",
            },
          ],
        },
      };
    },
    instrument_id_list() {
      return this.schedule.process_schedules.reduce(
        (acc, { process_schedule_instrument_instances }) => {
          process_schedule_instrument_instances.forEach(({ instrument_id }) => {
            const check = acc.find((id) => id === instrument_id);

            !check && (acc = [...acc, instrument_id]);
          });

          return acc;
        },
        []
      );
    },
    schedule_process_prefix() {
      return this.schedule.process_schedules[0].schedulable_process_type.substring(
        4,
        8
      );
    },
    schedule_status() {
      return this.schedule.current_schedule_status_log.schedule_status.status;
    },
    approved() {
      return (
        this.schedule.current_schedule_status_log.schedule_status.status ===
        "Approved"
      );
    },
    process_badges() {
      return this.schedule.process_schedules.length
        ? this.schedule.process_schedules
            .map(({ schedulable_process }) => schedulable_process.name)
            .sort()
        : [];
    },
    current_status() {
      return (
        this.schedule &&
        this.schedule.current_schedule_status_log.schedule_status.status
      );
    },
  },
  watch: {
    run_init: {
      handler() {
        if (!this.run_init) {
          return;
        }

        this.init();
        this.run_init = false;
      },
      deep: true,
      immediate: true,
    }
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    deleteSchedule() {
      return this.schedule.delete().then(() => {
        return this.$router.push("/schedules");
      });
    },
    download() {
      this.downloading = true;

      this.$refs["processDownload"].download().then(() => {
        this.downloading = false;
      });
    },
    upload() {
      this.$refs["processUpload"].chooseFile();
    },
    async init() {
      this.loading = true;

      this.schedule = new ScheduleModel({ id: this.schedule_id })
      await this.schedule.fetch({
        params: {
          connection: [
            "samples.submission.project.customer",
            "samples.sample_status_logs.sample_status",
          ],
        }
      })

      if (this.schedule) {
        this.instrument_jig_collection = await getCollection({
          collection_type: "instrument_jig",
          filters: [
            {
              logic: "filterIn",
              field: "instrument_id",
              value: this.instrument_id_list,
            },
          ],
          message: this.$bvToast,
        });
        this.schedule.sample_type_id &&
          (this.sample_type = await getModel({
            model_type: "sample_type",
            id: this.schedule.sample_type_id,
            message: this.$bvToast,
          })).name;

        this.sample_collection = this.schedule.getSamples();
        this.schedule_status_logs = this.schedule.getScheduleStatusLogs();
        this.loading = false;
      } else {
        this.$router.back();
      }
    },
    approve() {
      this.approved_loading = true;
      // auto schedule needs to run first before you can set the status
      this.runAutoScheduler().then(() => {
        this.schedule.setStatus("Approved").then(
          () => {
            this.quickSuccessToast(
              "Schedule Approved",
              "The status was set successfully."
            );
            
            this.approved_loading = false;
          },
          (err) => {
            this.quickWarningToast("Schedule Status Error", err);
          }
          )
        })
    },
    draft() {
      this.schedule.setStatus("Draft").then(
        () => {
          this.quickSuccessToast(
            "Schedule Drafted",
            "The status was set successfully."
          );

          this.init();
        },
        (err) => {
          this.quickWarningToast("Schedule Status Error", err);
        }
      );
    },
    decline() {
      this.schedule.setStatus("Declined").then(
        () => {
          this.quickSuccessToast(
            "Schedule Declined",
            "The status was set successfully."
          );
          
          this.clearAutoSchedule();

        },
        (err) => {
          this.quickWarningToast("Schedule Status Error", err);
        }
      );
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this submission. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await ScheduleRepository.suspend(this.schedule_id);

              this.deleteSuccess();
              this.$router.push("/schedules");
            } catch ({ response }) {
              const { status, data } = response.response;

              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
    async runAutoScheduler() {
      // set the auto schedule state to true
      this.auto_schedule_running = true;
      // run the auto schedule api route
      ScheduleRepository.autoSchedule(this.schedule_id).then(() =>{
        this.quickSuccessToast('Auto Scheduled Success', 'Schedule successfully auto scheduled.');
        // let the system know to run the init method
        this.run_init = true;
      }).catch(err => {
        console.error(err);
        // let the user know if the auto scheduler failed and a list of the reasons
        this.quickWarningToast('Auto Schedule Did Not Run', 'Check that you are in draft mode and that the auto schedule has not been run previously');
        // finally stop the auto schedule loading
      }).finally(() => this.auto_schedule_running = false);
    },
    clearAutoSchedule() {
      ScheduleRepository.clearSchedule(this.schedule_id).then(() => {
        this.quickSuccessToast('Schedule Cleared', 'The schedule has been cleared from the system');
        this.init();
      });
    },
  },
  created() {
    this.init();
  },
  notifications: scheduleNotifications,
};
</script>
